import { uniqueName } from '@/utils/fs/name.utils';
import generateID from '@/utils/generate-id';
import { defineStore } from 'pinia';
import { useLayerStore } from './use-layer-store';
import { useIfttt } from '@/hooks/use-ifttt';
const findThemeById = (themeId: string) =>
  useLayerStore().themes.find((item) => item.id === themeId);

export const useIFTTTStore = defineStore('ifttt-store', {
  getters: {
    // 根据shapeId查找ifttts
    ifttts() {
      return (themeId: string, shapeId: string) => {
        return findThemeById(themeId)?.ifttts?.filter(
          (item) => item.shapeId === shapeId
        ) as IFTTT[];
      };
    },
    ifttt() {
      return (iftttId: string) => {
        const ifttts = useLayerStore()
          .themes.map((item) => (item.ifttts ?? []) as IFTTT[])
          .filter((item) => !!item?.length)
          .flat();
        return ifttts.find((item) => item.id === iftttId);
      };
    }
  },
  actions: {
    addIFTTT(themeId: string, shapeId: string) {
      const theme = findThemeById(themeId);
      if (!theme) return;
      if (!theme.ifttts) theme.ifttts = [];
      const names = theme.ifttts
        .filter((item) => item.shapeId == shapeId)
        ?.map((item) => item.name);
      const name = uniqueName(names ?? [], '动效1');
      const { compareTypeOptions, animationTypeOptions } = useIfttt();
      const ifttt: IFTTT = {
        id: generateID(),
        shapeId,
        name,
        loop: 'none',
        loopDelay: 0,
        condition: {
          id: generateID(),
          children: [
            {
              id: generateID(),
              type:
                compareTypeOptions.find((item) => !item.disabled)?.value ||
                'when',
              logicalOperator: 'and',
              flag: 'compare',
              children: [
                {
                  id: generateID(),
                  whenOperator: 'MoreThanOrEqual',
                  whenSourceKey: 'timeHour',
                  whenValue: 9,
                  flag: 'compare-param'
                }
              ]
            }
          ]
        },
        animations: [
          {
            id: generateID(),
            type:
              animationTypeOptions.find((item) => !item.disabled)?.value ||
              'position',
            name: '效果1',
            duration: 300,
            delay: 0,
            easeType: 'Linear',
            translateX: 100,
            translateY: 100,
            alpha: 70,
            toAlpha: 100,
            scale: 50,
            rotation: 90
          }
        ]
      };

      theme.ifttts.push(ifttt);
      return ifttt;
    },
    /**
     * 根据id或shapeid删除ifttt
     * @param themeId
     * @param iftttIdOrShapeId iftttId或shapeId
     * @returns
     */
    deleteIFTTT(
      themeId: string,
      id: {
        iftttId?: string;
        shapeId?: string;
      }
    ) {
      const theme = findThemeById(themeId);
      if (!theme) return;
      if (!theme.ifttts) theme.ifttts = [];
      const { iftttId, shapeId } = id;
      theme.ifttts = theme.ifttts
        // 如果iftttId存在，则删除iftttId对应的ifttt
        .filter((item) => !iftttId || item.id != iftttId)
        // 如果shapeId存在，则删除shapeId对应的ifttt
        .filter((item) => !shapeId || item.shapeId != shapeId);
    },
    updateIFTTT(
      themeId: string,
      iftttId: string,
      attrs: Partial<Omit<IFTTT, 'condition' | 'animations'>>
    ) {
      const theme = findThemeById(themeId);
      if (!theme) return;
      const ifttt = theme.ifttts?.find((item) => item.id === iftttId);
      if (ifttt) {
        Object.assign(ifttt, attrs);
      }
    },
    addCondition(
      iftttId: string,
      data: {
        flag: 'compare' | 'compare-param';
        compareId?: string;
      }
    ) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;
      if (!ifttt.condition) {
        ifttt.condition = {
          id: generateID(),
          logicalOperator: 'and',
          children: []
        };
      }

      const _paramNode: CompareParam = {
        id: generateID(),
        whenOperator: 'MoreThanOrEqual',
        whenSourceKey: 'timeHour',
        whenValue: 9,
        flag: 'compare-param'
      };
      const { flag, compareId } = data;
      if (flag == 'compare-param') {
        if (!compareId) throw 'compareId is required';
        // 添加compare-param
        const compareNode = ifttt.condition.children.find(
          (item) => item.id === compareId
        ) as Compare | undefined;
        if (!compareNode) return;
        compareNode?.children?.push({ ..._paramNode });
      } else if (flag == 'compare') {
        // 添加compare
        ifttt.condition.children.push({
          id: generateID(),
          type: 'when',
          logicalOperator: 'and',
          flag: 'compare',
          children: [{ ..._paramNode }]
        });
      }
    },
    deleteCondition(iftttId: string, compareParamId: string) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;

      const compareNode = ifttt.condition?.children.find((compare) =>
        compare.children?.some((item) => item.id === compareParamId)
      );
      if (!compareNode) return;

      compareNode.children = compareNode.children?.filter(
        (item) => item.id != compareParamId
      );
      if (!compareNode.children?.length && ifttt.condition) {
        ifttt.condition.children = ifttt.condition.children?.filter(
          (item) => item.id != compareNode?.id
        );
      }
    },
    /**
     * 更新condition或compare上属性
     * @param iftttId
     * @param conditionOrcompareId
     * @param data
     * @returns
     */
    updateCondition(
      iftttId: string,
      nodeId: string,
      data: {
        logicalOperator?: Compare['logicalOperator'];
        compareType?: Compare['type'];
        whenSourceKey?: string;
        whenOperator?: CompareParam['whenOperator'];
        whenValue?: CompareParam['whenValue'];
      }
    ) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;
      const {
        logicalOperator,
        compareType,
        whenOperator,
        whenSourceKey,
        whenValue
      } = data;
      const condition =
        ifttt.condition.id == nodeId ? ifttt.condition : undefined;
      const compare = ifttt.condition.children.find(
        (item) => item.id === nodeId
      );

      const compareParam = ifttt.condition.children
        .map((item) => item.children)
        .flat()
        .find((item) => item?.id === nodeId);

      if (condition) {
        if (logicalOperator) condition.logicalOperator = logicalOperator;
      }
      if (compare) {
        if (logicalOperator) compare.logicalOperator = logicalOperator;
        if (compareType) compare.type = compareType;
      }
      if (compareParam) {
        if (whenOperator) compareParam.whenOperator = whenOperator;
        if (whenSourceKey != undefined)
          compareParam.whenSourceKey = whenSourceKey;
        if (whenValue != undefined) compareParam.whenValue = whenValue;
      }
    },
    deleteAnimation(iftttId: string, aniId: string) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;
      ifttt.animations = ifttt.animations.filter((item) => item.id != aniId);
    },
    addAnimation(iftttId: string, type?: IAnimation['type']) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;
      if (!ifttt.animations.length) ifttt.animations = [];
      const names = ifttt.animations.map((item) => item.name);
      const name = uniqueName(names ?? [], '效果1');
      const item: IAnimation = {
        type: type ?? 'position',
        name,
        duration: 300,
        delay: 0,
        easeType: 'Linear',
        // 不同动画默认值，
        translateX: 100,
        translateY: 100,
        alpha: 70,
        toAlpha: 100,
        scale: 50,
        rotation: 90,
        // pivotX: 0,
        // pivotY: 0,
        id: generateID()
      };
      ifttt.animations.push(item);
    },
    updateAnimation(
      iftttId: string,
      aniId: string,
      data: Partial<Omit<IAnimation, 'id'>>
    ) {
      const ifttt = this.ifttt(iftttId);
      if (!ifttt) return;
      const ani = ifttt.animations.find((item) => item.id == aniId);
      if (!ani) return;
      const keys = Object.keys(data);
      keys.forEach((key) => {
        const _key = key as 'name';
        const val = data[_key];
        if (['name', 'id'].includes(_key) && !val) return;
        ani[_key] = val as string;
      });
    }
  }
});
